import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createRootRoute,
  createRoute,
  createRouter,
  Navigate,
  Outlet,
  useNavigate,
  type ErrorComponentProps,
} from "@tanstack/react-router";

import { getCurrentUser } from "./api/client.js";
import { ApiError, displayError } from "./api/errors.js";
import { AuthScreen, ForgotPasswordScreen } from "./auth/auth-screen.js";
import { InvitationAcceptScreen } from "./auth/invitation-accept-screen.js";
import { pendingInvitationToken } from "./auth/invitations.js";
import { ErrorState, LoadingState } from "./components/states.js";
import { OrganisationOnboarding } from "./onboarding/organisation-onboarding.js";
import {
  AttentionPage,
  BillingPage,
  LoggingPage,
  PrivacyPage,
  RecordsPage,
  ReportsPage,
  SetupPage,
  SettingsPage,
  TasksPage,
  TeamPage,
  TodayPage,
} from "./pages/pages.js";
import { AppShell } from "./shell/app-shell.js";
import { LockScreen } from "./quick-access/lock-screen.js";
import { SharedDeviceGate } from "./quick-access/shared-device-gate.js";
import { PublicReportPage } from "./reports/public-report-page.js";

function RootComponent() {
  return <Outlet />;
}

function RootError({ error, reset }: ErrorComponentProps) {
  return (
    <main className="grid min-h-dvh place-items-center bg-canvas p-5">
      <div className="w-full max-w-2xl">
        <ErrorState message={displayError(error)} onAction={reset} />
      </div>
    </main>
  );
}

const rootRoute = createRootRoute({
  component: RootComponent,
  errorComponent: RootError,
  notFoundComponent: () => (
    <main className="grid min-h-dvh place-items-center bg-canvas p-5 text-center">
      <div>
        <p className="text-xs font-bold tracking-widest text-emerald-800 uppercase">
          404
        </p>
        <h1 className="mt-3 text-3xl font-black tracking-tight">
          That page isn’t here
        </h1>
        <p className="mt-2 text-sm text-stone-500">
          Return to SafePlate’s Today screen.
        </p>
      </div>
    </main>
  ),
});

function AuthRoute({ mode }: { mode: "sign-in" | "sign-up" }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return (
    <AuthScreen
      mode={mode}
      onAuthenticated={async () => {
        await queryClient.invalidateQueries({ queryKey: ["me"] });
        await navigate({ to: "/" });
      }}
    />
  );
}

const signInRoute = createRoute({
  component: () => <AuthRoute mode="sign-in" />,
  getParentRoute: () => rootRoute,
  path: "/sign-in",
});

const signUpRoute = createRoute({
  component: () => <AuthRoute mode="sign-up" />,
  getParentRoute: () => rootRoute,
  path: "/sign-up",
});

const forgotPasswordRoute = createRoute({
  component: ForgotPasswordScreen,
  getParentRoute: () => rootRoute,
  path: "/forgot-password",
});

function AuthCallbackRoute() {
  const invitationToken = pendingInvitationToken();
  return invitationToken ? (
    <Navigate
      replace
      search={{ token: invitationToken }}
      to="/invitations/accept"
    />
  ) : (
    <Navigate replace to="/" />
  );
}

const authCallbackRoute = createRoute({
  component: AuthCallbackRoute,
  getParentRoute: () => rootRoute,
  path: "/auth/callback",
});

const invitationAcceptRoute = createRoute({
  component: function InvitationRoute() {
    const search = invitationAcceptRoute.useSearch();
    return (
      <InvitationAcceptScreen
        {...(search.token ? { token: search.token } : {})}
      />
    );
  },
  getParentRoute: () => rootRoute,
  path: "/invitations/accept",
  validateSearch: (search: Record<string, unknown>) => ({
    ...(typeof search.token === "string" ? { token: search.token } : {}),
  }),
});

const lockRoute = createRoute({
  component: LockScreen,
  getParentRoute: () => rootRoute,
  path: "/lock",
});

const publicReportRoute = createRoute({
  component: () => {
    const { token, slug, period } = publicReportRoute.useParams();
    return <PublicReportPage token={token} slug={slug} period={period} />;
  },
  getParentRoute: () => rootRoute,
  path: "/share/$slug/report/$period/$token",
});
const publicReportSectionRoute = createRoute({
  component: () => {
    const { token, slug, period, section } =
      publicReportSectionRoute.useParams();
    return (
      <PublicReportPage
        token={token}
        slug={slug}
        period={period}
        section={section}
      />
    );
  },
  getParentRoute: () => rootRoute,
  path: "/share/$slug/report/$period/$token/$section",
});

function AuthenticatedArea() {
  const meQuery = useQuery({
    queryFn: getCurrentUser,
    queryKey: ["me"],
    retry(failureCount, error) {
      return (
        !(error instanceof ApiError && error.status === 401) && failureCount < 2
      );
    },
    staleTime: 60_000,
  });

  if (meQuery.isPending) {
    return (
      <main className="grid min-h-dvh place-items-center bg-canvas p-5">
        <div className="w-full max-w-xl">
          <LoadingState label="Opening SafePlate" />
        </div>
      </main>
    );
  }
  if (meQuery.error) {
    if (meQuery.error instanceof ApiError && meQuery.error.status === 401) {
      return <Navigate replace to="/lock" />;
    }
    return (
      <main className="grid min-h-dvh place-items-center bg-canvas p-5">
        <div className="w-full max-w-2xl">
          <ErrorState
            message={displayError(meQuery.error)}
            onAction={() => void meQuery.refetch()}
            title="SafePlate didn’t open"
          />
        </div>
      </main>
    );
  }

  if (meQuery.data.memberships.length === 0) {
    return (
      <OrganisationOnboarding
        me={meQuery.data}
        onCreated={async () => {
          await meQuery.refetch();
        }}
      />
    );
  }

  if (meQuery.data.session.auth_method === "shared_pin") {
    return <AppShell me={meQuery.data} />;
  }

  return (
    <SharedDeviceGate me={meQuery.data}>
      {(scopedMe) => <AppShell me={scopedMe} />}
    </SharedDeviceGate>
  );
}

const authenticatedRoute = createRoute({
  component: AuthenticatedArea,
  getParentRoute: () => rootRoute,
  id: "_authenticated",
});

const todayRoute = createRoute({
  component: TodayPage,
  getParentRoute: () => authenticatedRoute,
  path: "/",
});
const recordsRoute = createRoute({
  component: RecordsPage,
  getParentRoute: () => authenticatedRoute,
  path: "/records",
});
const loggingRoute = createRoute({
  component: LoggingPage,
  getParentRoute: () => authenticatedRoute,
  path: "/log",
});
const attentionRoute = createRoute({
  component: AttentionPage,
  getParentRoute: () => authenticatedRoute,
  path: "/attention",
});
const tasksRoute = createRoute({
  component: TasksPage,
  getParentRoute: () => authenticatedRoute,
  path: "/tasks",
});
const teamRoute = createRoute({
  component: TeamPage,
  getParentRoute: () => authenticatedRoute,
  path: "/team",
});
const setupRoute = createRoute({
  component: SetupPage,
  getParentRoute: () => authenticatedRoute,
  path: "/setup",
});
const reportsRoute = createRoute({
  component: ReportsPage,
  getParentRoute: () => authenticatedRoute,
  path: "/reports",
});
const billingRoute = createRoute({
  component: BillingPage,
  getParentRoute: () => authenticatedRoute,
  path: "/billing",
});
const billingSuccessRoute = createRoute({
  component: BillingPage,
  getParentRoute: () => authenticatedRoute,
  path: "/billing/success",
});
const privacyRoute = createRoute({
  component: PrivacyPage,
  getParentRoute: () => authenticatedRoute,
  path: "/privacy",
  validateSearch: (search: Record<string, unknown>) => ({
    ...(typeof search.attachment_id === "string"
      ? { attachment_id: search.attachment_id }
      : {}),
    ...(typeof search.staff_profile_id === "string"
      ? { staff_profile_id: search.staff_profile_id }
      : {}),
  }),
});
const settingsRoute = createRoute({
  component: SettingsPage,
  getParentRoute: () => authenticatedRoute,
  path: "/settings",
});

const routeTree = rootRoute.addChildren([
  signInRoute,
  signUpRoute,
  forgotPasswordRoute,
  authCallbackRoute,
  invitationAcceptRoute,
  lockRoute,
  publicReportRoute,
  publicReportSectionRoute,
  authenticatedRoute.addChildren([
    todayRoute,
    recordsRoute,
    loggingRoute,
    attentionRoute,
    tasksRoute,
    teamRoute,
    setupRoute,
    reportsRoute,
    billingRoute,
    billingSuccessRoute,
    privacyRoute,
    settingsRoute,
  ]),
]);

export const router = createRouter({
  defaultPreload: "intent",
  defaultPreloadStaleTime: 30_000,
  routeTree,
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
